import {
  SET_ASSET_TRANSACTIONS,
  APPEND_ASSET_TRANSACTIONS,
  SET_ACTIVE_FILTERS,
  SET_ACTIVE_SORTING,
  CLEAR_ACTIVE_FILTERS,
  UPDATE_ASSET_TRANSACTION_RECORD,
  REMOVE_ASSET_TRANSACTION
} from './constants.ts'
import { AssetTransactionRecord, AssetTransactionRecordFields } from '../../data_models/asset_transaction_record.ts'
import { FilterValue, SortConfig } from '../../../app_modules/global_configs/tableViewPreferences.ts'

export interface AssetTransactionsPaginatedResponse {
  data: AssetTransactionRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface AssetTransactionsState {
  data: AssetTransactionRecord[]
  activeFilters: FilterValue[]
  activeSorting: SortConfig[]
  hasMore: boolean
  currentPage: number
}

type AssetTransactionsAction =
  | { type: typeof SET_ASSET_TRANSACTIONS; payload: { response: AssetTransactionsPaginatedResponse } }
  | { type: typeof APPEND_ASSET_TRANSACTIONS; payload: { response: AssetTransactionsPaginatedResponse } }
  | { type: typeof SET_ACTIVE_FILTERS; payload: { filters: FilterValue[] } }
  | { type: typeof SET_ACTIVE_SORTING; payload: { sorting: SortConfig[] } }
  | { type: typeof CLEAR_ACTIVE_FILTERS }
  | { type: typeof UPDATE_ASSET_TRANSACTION_RECORD; payload: { transactionId: string; updates: Record<string, any> } }
  | { type: typeof REMOVE_ASSET_TRANSACTION; payload: { transactionId: string } }

const assetTransactionsReducer = (
  state: AssetTransactionsState = {
    data: [],
    activeFilters: [],
    activeSorting: [],
    hasMore: false,
    currentPage: 1
  },
  action: AssetTransactionsAction
): AssetTransactionsState => {
  const idKey = AssetTransactionRecordFields.asset_transactions__id
  switch (action.type) {
    case SET_ASSET_TRANSACTIONS: {
      const { response } = action.payload
      return { ...state, data: response.data, hasMore: response.hasMore, currentPage: 1 }
    }

    case APPEND_ASSET_TRANSACTIONS: {
      const { response } = action.payload
      return {
        ...state,
        data: [...state.data, ...response.data],
        hasMore: response.hasMore,
        currentPage: state.currentPage + 1
      }
    }

    case SET_ACTIVE_FILTERS: {
      return { ...state, activeFilters: action.payload.filters }
    }

    case SET_ACTIVE_SORTING: {
      return { ...state, activeSorting: action.payload.sorting }
    }

    case CLEAR_ACTIVE_FILTERS: {
      return { ...state, activeFilters: [] }
    }

    case UPDATE_ASSET_TRANSACTION_RECORD: {
      const { transactionId, updates } = action.payload
      if (!state.data.some((r) => (r as any)[idKey] === transactionId)) return state
      return {
        ...state,
        data: state.data.map((r) => ((r as any)[idKey] === transactionId ? { ...r, ...updates } : r))
      }
    }

    case REMOVE_ASSET_TRANSACTION: {
      const { transactionId } = action.payload
      return { ...state, data: state.data.filter((r) => (r as any)[idKey] !== transactionId) }
    }

    default:
      return state
  }
}

export default assetTransactionsReducer

export const setAssetTransactions = (payload: { response: AssetTransactionsPaginatedResponse }) => ({
  type: SET_ASSET_TRANSACTIONS,
  payload
})

export const appendAssetTransactions = (payload: { response: AssetTransactionsPaginatedResponse }) => ({
  type: APPEND_ASSET_TRANSACTIONS,
  payload
})

export const setAssetTransactionsActiveFilters = (payload: { filters: FilterValue[] }) => ({
  type: SET_ACTIVE_FILTERS,
  payload
})

export const setAssetTransactionsActiveSorting = (payload: { sorting: SortConfig[] }) => ({
  type: SET_ACTIVE_SORTING,
  payload
})

export const clearAssetTransactionsActiveFilters = () => ({ type: CLEAR_ACTIVE_FILTERS })

export const updateAssetTransactionRecord = (payload: { transactionId: string; updates: Record<string, any> }) => ({
  type: UPDATE_ASSET_TRANSACTION_RECORD,
  payload
})

export const removeAssetTransaction = (payload: { transactionId: string }) => ({
  type: REMOVE_ASSET_TRANSACTION,
  payload
})

export const selectAssetTransactionById = (state: any, transactionId: string) =>
  (state.assetTransactions?.data ?? []).find(
    (r: any) => r[AssetTransactionRecordFields.asset_transactions__id] === transactionId
  )
